import type { MouseEvent } from 'react';

import { Dialog, DialogContent, Sheet, SheetContent } from 'design-system';
import { useMatch } from 'react-router';

import { Z_INDEX } from '@/config/z-index';
import FactsheetView from '@/features/factsheet/components/FactsheetView';
import {
  FactsheetSelectionContext,
  type FactsheetSelectionState
} from '@/features/factsheet/state/FactsheetSelectionContext';
import { useFactsheetOpenState } from '@/features/factsheet/state/useFactsheetOpenState';
import { useFactsheetOverlayContainer } from '@/features/factsheet/state/useFactsheetOverlayContainer';

interface FactsheetDialogProps {
  selectionState?: FactsheetSelectionState;
}

const FactsheetDialog = ({ selectionState }: FactsheetDialogProps) => {
  const { container } = useFactsheetOverlayContainer();
  const { code, additionalTabs, close, setOpenTabs } = useFactsheetOpenState();
  const isCatalogRoute = Boolean(useMatch('/catalog/*'));

  const isWaitingForCatalogTable = isCatalogRoute && container === null;
  const open = code !== null && !isWaitingForCatalogTable;

  const factsheetKeys = code ? [code, ...additionalTabs].join(',') : '';

  // The full-screen DialogContent fills the viewport, so base-ui's own
  // outside-press-on-backdrop never fires (there's no backdrop area visible
  // to click). Close only when the click lands directly on this flex
  // container, not on the centered factsheet card inside it.
  const handleBackdropClick = (event: MouseEvent<HTMLDivElement>): void => {
    if (event.target === event.currentTarget) {
      close();
    }
  };

  if (!open) {
    return null;
  }

  const factsheetSelection = (
    <FactsheetSelectionContext value={selectionState ?? null}>
      <FactsheetView
        factsheetKeys={factsheetKeys}
        onDialogClose={close}
        onOpenTabsChange={setOpenTabs}
      />
    </FactsheetSelectionContext>
  );

  // Scoped mode: a right-anchored panel over the claimed container (e.g. the catalog
  // table) instead of a full-viewport modal. `modal={false}` skips scroll-lock/focus-trap
  // and leaves the rest of the page interactive; `hideOverlay` skips the backdrop so the
  // table stays clickable behind the panel.
  if (container) {
    return (
      <Sheet
        open={open}
        modal={false}
        disablePointerDismissal
        onOpenChange={openState => {
          if (!openState) close();
        }}
      >
        <SheetContent
          container={container}
          hideOverlay
          showCloseButton={false}
          side='right'
          className='absolute inset-y-0 h-full data-[side=right]:w-[633px] data-[side=right]:border-l-2 data-[side=right]:sm:max-w-none! border-b-2 border-primary bg-transparent shadow-none'
          style={{ zIndex: Z_INDEX.floatingAnchor }}
        >
          {factsheetSelection}
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={openState => {
        if (!openState) close();
      }}
    >
      <DialogContent
        showCloseButton={false}
        className='inset-0 top-0 left-0 flex h-screen max-h-screen w-screen max-w-none sm:max-w-none! translate-x-0 translate-y-0 items-stretch justify-center rounded-none bg-gx-additional-paper/80 p-0 ring-0'
        onClick={handleBackdropClick}
      >
        {factsheetSelection}
      </DialogContent>
    </Dialog>
  );
};

export default FactsheetDialog;
